import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { InfoScreenData } from '../types/InfoScreenData';
import { ErrorMessageComponent } from './error-message/error-message.component';
import { InfoscreenHeaderComponent } from './infoscreen-header/infoscreen-header.component';
import { InfoscreenFooterComponent } from './infoscreen-footer/infoscreen-footer.component';
import { ActivityTableComponent } from './activity-table/activity-table.component';
import { CalendarViewComponent } from '../school-calendar/calendar-view.component';
import { CalendarField, CalendarPeriod } from '../school-calendar/school-calendar.types';
import { LegacyView } from '../infoscreen-smart/legacy-view';

@Component({
  selector: 'app-infoscreen-dumb',
  imports: [
    CommonModule,
    ErrorMessageComponent,
    InfoscreenHeaderComponent,
    InfoscreenFooterComponent,
    ActivityTableComponent,
    CalendarViewComponent,
  ],
  templateUrl: './infoscreen.component.html',
  styleUrls: ['./infoscreen.component.scss'],
})
export class InfoscreenComponent implements OnInit, AfterViewInit, OnDestroy {
  // Scroll speed multiplier: 0.04 = 4% of viewport height per second
  private readonly SCROLL_SPEED_MULTIPLIER = 0.03;

  @ViewChild('tableContainer', { read: ElementRef })
  tableContainer!: ElementRef<HTMLElement>;

  clockIntervalId: any;
  progressCycleIntervalId: any;
  currentTime: string = '';
  @Output() onProgressCycleEnd = new EventEmitter<string>();
  values = input<InfoScreenData[]>();
  locationName = input<string>();
  spinnerState = input<any>();
  hasLocationId = input<boolean>(true);
  date = input.required<Date>();
  /** The list (the default), or the school timetable for the day or the week (`?view=`). */
  view = input<LegacyView>('list');
  periods = input<CalendarPeriod[]>([]);
  from = input<string>('');

  /** The lines in the timetable's boxes: as the module's defaults for the day and the week. */
  readonly dayFields: CalendarField[] = ['Time', 'Activity', 'Level', 'Note', 'LockerRooms'];
  readonly weekFields: CalendarField[] = ['Time', 'Activity', 'Level', 'Facility', 'Note', 'LockerRooms'];
  progressValue = signal(0);
  timingInSeconds = 60;

  // Auto-scroll state
  shouldScroll = signal(false);
  scrollDurationSeconds = signal(20);
  isRefreshing = signal(false);
  isReady = signal(false); // Control initial fade-in

  displayedColumns: string[] = ['time', 'activity', 'location', 'title'];

  dataSource: InfoScreenData[] = [];

  constructor() {
    effect(() => {
      this.dataSource = this.values() || [];

      // Trigger refresh transition when data changes
      if (this.isReady()) {
        this.handleDataRefresh();
      }
    });
  }

  ngOnInit(): void {
    this.startProgressCycle();
  }

  startProgressCycle() {
    this.progressValue.set(0);
    const interval = 100;
    const totalIntervals = (this.timingInSeconds * 1000) / interval;

    let elapsedIntervals = 0;
    setInterval(() => {
      elapsedIntervals++;
      this.progressValue.set(
        parseFloat(((elapsedIntervals / totalIntervals) * 100).toFixed(2)),
      );

      if (elapsedIntervals >= totalIntervals) {
        this.onProgressCycleEnd.emit('endOfCycle');
        elapsedIntervals = 0;
      }
    }, interval);
  }

  ngAfterViewInit(): void {
    // Wait for layout to complete before checking scroll
    setTimeout(() => {
      this.checkScrollNeeded();

      // Fade in after initial setup
      setTimeout(() => {
        this.isReady.set(true);
      }, 200);
    }, 0);
  }

  private checkScrollNeeded(): void {
    if (!this.tableContainer) return;

    const container = this.tableContainer.nativeElement;
    const clientHeight = container.clientHeight;

    // Always measure only the first table to ensure consistent duration calculation
    // Whether we have one or two tables in DOM, we only need the height of one
    const firstTable = container.querySelector('mat-table');
    if (!firstTable) return;

    const tableHeight = (firstTable as HTMLElement).offsetHeight;

    if (tableHeight > clientHeight) {
      // Calculate dynamic duration based on viewport-relative speed
      // Formula: tableHeight / (clientHeight * SCROLL_SPEED_MULTIPLIER)
      // This ensures consistent visual speed across different screen sizes
      const duration =
        tableHeight / (clientHeight * this.SCROLL_SPEED_MULTIPLIER);

      this.scrollDurationSeconds.set(Math.max(10, duration)); // Minimum 10 seconds

      this.shouldScroll.set(true);
    } else {
      this.shouldScroll.set(false);
    }
  }

  private handleDataRefresh(): void {
    if (!this.tableContainer) return;

    // Trigger fade-out transition
    this.isRefreshing.set(true);

    // Wait for fade-out, then recalculate scroll
    setTimeout(() => {
      this.checkScrollNeeded();

      // Fade-in after recalculation
      setTimeout(() => {
        this.isRefreshing.set(false);
      }, 100);
    }, 300);
  }

  ngOnDestroy() {
    clearInterval(this.clockIntervalId);
    clearInterval(this.progressCycleIntervalId);
  }
}
